/**
 * CapabilityEditorCard
 * 
 * Interactive card for selecting customers and products in the agent chat.
 * Provides search and selection functionality for structured data extraction.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { searchProducts, getRivhitItemPricing } from '@/api/client';
import { useRivhitData, rivhitDisplayName } from '@/context/RivhitDataContext';
import { pickBestCustomerPrice, extractRivhitCustomerId } from '@/lib/pricing';
import type {
  CapabilityEditorCardData,
  CapabilityItem,
  CustomerOption,
  OptionChoice,
  ProductOption,
} from '../types';

interface CapabilityEditorCardProps {
  data: CapabilityEditorCardData;
  onSelect: (selection: Record<string, unknown>) => void;
}

export function CapabilityEditorCard({ data, onSelect }: CapabilityEditorCardProps) {
  const { configured, searchCustomers: searchRivhit, ensureCustomersLoaded } = useRivhitData();
  const [selectedDocumentOptionId, setSelectedDocumentOptionId] = useState<string | undefined>(
    data.selected_document_option_id || data.document_options[0]?.option_id,
  );
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | undefined>(data.customer);
  const [items, setItems] = useState<CapabilityItem[]>(data.items ?? []);
  const [notes, setNotes] = useState(data.notes || '');
  const [customerQuery, setCustomerQuery] = useState('');
  const [itemQuery, setItemQuery] = useState('');
  const [customerResults, setCustomerResults] = useState<CustomerOption[]>([]);
  const [itemResults, setItemResults] = useState<ProductOption[]>([]);

  useEffect(() => {
    if (!configured) return;
    ensureCustomersLoaded();
  }, [configured, ensureCustomersLoaded]);

  const resolveItemPrice = useCallback(
    async (productId: string, quantity: number, fallbackPrice: number): Promise<number> => {
      const rivhitCustomerId = selectedCustomer ? extractRivhitCustomerId(selectedCustomer.customer_id) : null;
      const rivhitItemId = extractRivhitCustomerId(productId);
      
      if (!rivhitCustomerId || !rivhitItemId) return fallbackPrice;

      try {
        const response = await getRivhitItemPricing(rivhitItemId, rivhitCustomerId);
        if (response.data && Array.isArray(response.data)) {
          return pickBestCustomerPrice(response.data, quantity, fallbackPrice);
        }
        return fallbackPrice;
      } catch {
        return fallbackPrice;
      }
    },
    [selectedCustomer],
  );

  useEffect(() => {
    if (!customerQuery.trim()) {
      setCustomerResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      try {
        const results = searchRivhit(customerQuery).slice(0, 8).map((customer) => ({
          customer_id: String(customer.customer_id),
          name: rivhitDisplayName(customer),
          phone: customer.phone,
          email: customer.email,
        }));
        setCustomerResults(results);
      } catch {
        setCustomerResults([]);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [customerQuery, searchRivhit]);

  useEffect(() => {
    if (!itemQuery.trim()) {
      setItemResults([]);
      return;
    }
    const timer = window.setTimeout(async () => {
      try {
        const response = await searchProducts(itemQuery, 8);
        const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
        const results = rows.map((product: Record<string, unknown>) => ({
          product_id: String(product.product_id || product.id || ''),
          name: String(product.name || ''),
          name_he: product.name_he as string | undefined,
          category: product.category as string | undefined,
          price: product.price as number | undefined,
          unit: (product.unit as string | undefined) || 'kg',
        }));
        setItemResults(results);
      } catch {
        setItemResults([]);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [itemQuery]);

  const customerOptions = useMemo(() => {
    const merged = [...(data.customer_suggestions || []), ...customerResults];
    const seen = new Set<string>();
    return merged.filter((customer) => {
      if (!customer.customer_id || seen.has(customer.customer_id)) return false;
      seen.add(customer.customer_id);
      return true;
    });
  }, [data.customer_suggestions, customerResults]);

  const productOptions = useMemo(() => {
    const merged = [...(data.item_suggestions || []), ...itemResults];
    const seen = new Set<string>();
    return merged.filter((product) => {
      if (!product.product_id || seen.has(product.product_id)) return false;
      seen.add(product.product_id);
      return true;
    });
  }, [data.item_suggestions, itemResults]);

  const addItem = async (product: ProductOption) => {
    const price = await resolveItemPrice(product.product_id, product.suggested_quantity || 1, product.price || 0);
    
    setItems((current) => {
      const existing = current.find((item) => item.product_id === product.product_id);
      if (existing) {
        return current.map((item) =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + (product.suggested_quantity || 1) }
            : item,
        );
      }
      return [
        ...current,
        {
          product_id: product.product_id,
          name: product.name,
          quantity: product.suggested_quantity || 1,
          unit: product.unit || 'kg',
          price,
        },
      ];
    });
  };

  const updateItem = useCallback(
    async (index: number, patch: Partial<CapabilityItem>) => {
      setItems((current) => {
        const next = [...current];
        next[index] = { ...next[index], ...patch };
        return next;
      });

      // If quantity changed, re-resolve price
      if (patch.quantity !== undefined) {
        const item = items[index];
        if (item && item.product_id) {
          const newPrice = await resolveItemPrice(item.product_id, patch.quantity, item.price || 0);
          setItems((current) => {
            const next = [...current];
            if (next[index]) {
              next[index] = { ...next[index], price: newPrice };
            }
            return next;
          });
        }
      }
    },
    [items, resolveItemPrice],
  );

  // Re-resolve all prices when customer changes
  useEffect(() => {
    if (items.length === 0) return;
    
    let cancelled = false;
    const repriceAll = async () => {
      try {
        const repriced = await Promise.all(
          items.map(async (item) => {
            if (!item.product_id) return item;
            const price = await resolveItemPrice(item.product_id, item.quantity, item.price || 0);
            return { ...item, price };
          })
        );
        if (!cancelled) {
          setItems(repriced);
        }
      } finally {
        // logic removed
      }
    };

    void repriceAll();
    return () => { cancelled = true; };
  }, [selectedCustomer?.customer_id]);

  const removeItem = (index: number) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const selectedOption: OptionChoice | undefined = data.document_options.find(
    (option) => option.option_id === selectedDocumentOptionId,
  );

  return (
    <div className="my-2 overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-sm">
      <div className="border-b border-blue-100 bg-blue-50 px-4 py-3">
        <p className="text-xs uppercase tracking-[0.18em] text-blue-600">Detected Capabilities</p>
        <h3 className="mt-1 text-sm font-semibold text-slate-900">Review and edit before drafting</h3>
        {data.context && <p className="mt-1 text-xs text-slate-500">{data.context}</p>}
      </div>

      {(data.detected_capabilities?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-2 border-b border-slate-100 px-4 py-3">
          {data.detected_capabilities?.map((capability) => (
            <div key={capability.id} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
              <span className="font-medium">{capability.label}:</span> {capability.value}
              {typeof capability.confidence === 'number' && (
                <span className="ml-1 text-slate-500">{Math.round(capability.confidence * 100)}%</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4 px-4 py-4">
        <section>
          <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">Document type</p>
          <div className="flex flex-wrap gap-2">
            {data.document_options.map((option) => (
              <button
                key={option.option_id}
                type="button"
                onClick={() => setSelectedDocumentOptionId(option.option_id)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  selectedDocumentOptionId === option.option_id
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {option.icon ? `${option.icon} ` : ''}
                {option.title}
              </button>
            ))}
          </div>
          {selectedOption?.description && <p className="mt-2 text-xs text-slate-500">{selectedOption.description}</p>}
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Customer</p>
            {selectedCustomer && <span className="text-xs text-slate-400">Selected</span>}
          </div>
          {selectedCustomer && (
            <div className="mb-2 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-800">
              <div className="font-medium">{selectedCustomer.name}</div>
              <div className="text-xs text-slate-500">
                {[selectedCustomer.phone, selectedCustomer.email].filter(Boolean).join(' • ')}
              </div>
            </div>
          )}
          <input
            type="text"
            value={customerQuery}
            onChange={(event) => setCustomerQuery(event.target.value)}
            placeholder="Search customer..."
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
          <div className="mt-2 max-h-36 space-y-1 overflow-y-auto">
            {customerOptions.map((customer) => (
              <button
                key={customer.customer_id}
                type="button"
                onClick={() => setSelectedCustomer(customer)}
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-left text-sm transition hover:border-primary-300 hover:bg-primary-50"
              >
                <div>
                  <div className="font-medium text-slate-800">{customer.name}</div>
                  <div className="text-xs text-slate-500">{[customer.phone, customer.email].filter(Boolean).join(' • ')}</div>
                </div>
                {typeof customer.confidence === 'number' && (
                  <span className="text-xs text-slate-500">{Math.round(customer.confidence * 100)}%</span>
                )}
              </button>
            ))}
          </div>
        </section>

        <section>
          <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">Items</p>
          <input
            type="text"
            value={itemQuery}
            onChange={(event) => setItemQuery(event.target.value)}
            placeholder="Search item..."
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
          <div className="mt-2 max-h-36 space-y-1 overflow-y-auto">
            {productOptions.map((product) => (
              <button
                key={product.product_id}
                type="button"
                onClick={() => addItem(product)}
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-left text-sm transition hover:border-primary-300 hover:bg-primary-50"
              >
                <div>
                  <div className="font-medium text-slate-800">{product.name}</div>
                  <div className="text-xs text-slate-500">
                    {[product.category, product.price != null ? `${product.price}₪/${product.unit || 'kg'}` : undefined]
                      .filter(Boolean)
                      .join(' • ')}
                  </div>
                </div>
                <span className="text-xs text-primary-700">Add</span>
              </button>
            ))}
          </div>

          <div className="mt-3 space-y-2">
            {items.map((item, index) => (
              <div key={`${item.product_id || item.name}-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="flex items-center justify-between gap-2">
                  <input
                    type="text"
                    value={item.name}
                    onChange={(event) => updateItem(index, { name: event.target.value })}
                    className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-primary-400"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="rounded-lg px-2 py-1 text-xs font-medium text-slate-500 transition hover:bg-rose-50 hover:text-rose-700"
                  >
                    Remove
                  </button>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={item.quantity}
                    onChange={(event) => updateItem(index, { quantity: parseFloat(event.target.value) || 0 })}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-primary-400"
                  />
                  <input
                    type="text"
                    value={item.unit || ''}
                    onChange={(event) => updateItem(index, { unit: event.target.value })}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-primary-400"
                  />
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={item.price ?? ''}
                    onChange={(event) => updateItem(index, { price: parseFloat(event.target.value) || 0 })}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-primary-400"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">Notes</p>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </section>
      </div>

      <div className="flex gap-2 border-t border-slate-100 px-4 py-3">
        <button
          type="button"
          onClick={() =>
            onSelect({
              action: 'apply',
              document_option_id: selectedDocumentOptionId,
              document_option_title: selectedOption?.title,
              customer_id: selectedCustomer?.customer_id,
              customer_name: selectedCustomer?.name,
              items: items.map((item) => ({
                product_id: item.product_id,
                item_id: item.item_id,
                name: item.name,
                quantity: item.quantity,
                unit: item.unit,
                price: item.price,
              })),
              notes,
            })
          }
          className="flex-1 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-700"
        >
          Apply draft
        </button>
        <button
          type="button"
          onClick={() => onSelect({ action: 'cancel' })}
          className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
